import { useCallback, useEffect } from 'react';

import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchPutFormalOffersCreditFacilityConfiguration,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration,
    PutFormalOffersCreditFacilityConfigurationRequestBody
} from '@iwoca/lapi-client/edge';
import { useQueryClient } from '@tanstack/react-query';

import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

type TOfferStep = NonNullable<
    NonNullable<
        NonNullable<PutFormalOffersCreditFacilityConfigurationRequestBody>['data']
    >['step']
>;

export const useSaveOfferStep = () => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    return useCallback(
        ({
            formalOffer,
            step,
            amount,
            sweep
        }: {
            formalOffer: TFormalOffer;
            step: TOfferStep;
            amount: number;
            sweep?: number;
        }) => {
            const offerId = formalOffer.offer_id!;

            const request =
                formalOffer.product_type === 'merchant_cash_advance'
                    ? fetchPutFormalOffersMerchantCashAdvanceConfiguration({
                          stateKey,
                          offerId,
                          body: {
                              data: {
                                  cash_amount: amount,
                                  step,
                                  revenue_sweep_fraction: sweep
                              }
                          }
                      })
                    : fetchPutFormalOffersCreditFacilityConfiguration({
                          stateKey,
                          offerId,
                          body: { data: { cash_amount: amount, step } }
                      });

            return request
                .then(() => {
                    tracking.ampli.stepPersisted({ offer_id: offerId, step });
                    sessionStorage.setItem(
                        `offer_flow_restored_${offerId}`,
                        'true'
                    );
                    return queryClient.invalidateQueries({
                        queryKey: [
                            'fetchGetFormalOffersLatestOfferConfiguration',
                            stateKey
                        ]
                    });
                })
                .catch((error) =>
                    IwDataDogLogger.logError(
                        'Failed to write offer flow memory step',
                        { tags: { reporter: 'offer_flow_memory', step } },
                        error as Error
                    )
                );
        },
        [stateKey, queryClient]
    );
};

export const useWriteOfferStepOnMount = ({
    offerId,
    step,
    amount,
    sweep
}: {
    offerId: string | undefined;
    step: TOfferStep;
    amount: number | undefined;
    sweep?: number;
}): void => {
    const saveOfferStep = useSaveOfferStep();
    const { formalOffer } = useGetFormalOffer(offerId);

    useEffect(() => {
        if (!formalOffer || amount === undefined || Number.isNaN(amount))
            return;

        saveOfferStep({ formalOffer, step, amount, sweep });
    }, [saveOfferStep, formalOffer, step, amount, sweep]);
};
